import React from 'react'
import { Checkbox } from '@mui/material'
import { AppConstants } from 'AppConstants/AppConstants'
import styles from './SmsConsent.module.scss'

export interface SmsConsentProps {
  /**
   * Paid-ad traffic (meta/google): render the original inline disclosure instead
   * of the consent checkboxes, exactly as the funnel read before A2P 10DLC.
   */
  legacy?: boolean
  transactional: boolean
  promotional: boolean
  onTransactionalChange: (checked: boolean) => void
  onPromotionalChange: (checked: boolean) => void
}

// Same control the login page's "I agree to receive appointment reminders"
// checkbox used (84b2fc4), aligned to the first line of the consent copy
const checkboxSx = {
  color: 'var(--color-primary)',
  '&.Mui-checked': {
    color: 'var(--color-primary)',
  },
  marginLeft: '-10px',
  alignSelf: 'flex-start',
  paddingTop: 0,
  paddingBottom: 0,
}

/**
 * The wording rendered on screen, as plain prose.
 *
 * Single source of truth for what the page shows *and* for what gets posted as
 * `smsConsentText`: an opt-in record is only evidence if it quotes the words
 * the person actually read, and the ad-traffic funnel shows different words
 * from the organic one.
 */
export const SMS_CONSENT_COPY = {
  legacy:
    "100% secure. By submitting, you agree to KIRO's Terms of Use and Privacy Policy. You consent to receive text " +
    'messages from us at the mobile number provided to book your appointment and receive reminders. Message frequency ' +
    'depends on your activity. Reply STOP to opt-out.',
  transactional:
    'You agree to receive automated transactional messages (appointment confirmations, reminders and verification ' +
    'codes) with varying frequency from KIRO Health at the mobile number provided. Consent is not a condition of ' +
    'purchase or account creation. Msg & data rates may apply. Reply STOP to end. Text HELP for help.',
  promotional:
    'You agree to receive automated promotional messages with varying frequency from KIRO Health at the mobile number ' +
    'provided. Consent is not a condition of purchase or account creation. Msg & data rates may apply. Reply STOP to ' +
    'end. Text HELP for help.',
}

// On screen these are links; in the stored record the URLs have to be spelled
// out, since a saved sentence cannot be clicked.
const LEGAL_SUFFIX = `Click for Terms of Service (${AppConstants.TERMS_URL}), Privacy Policy (${AppConstants.PRIVACY_URL}).`

/** Exactly what this render put in front of the person, for the audit record. */
export const smsConsentTextShown = ({ legacy, promotional }: { legacy?: boolean; promotional?: boolean }): string => {
  if (legacy) return SMS_CONSENT_COPY.legacy
  const shown = [`${SMS_CONSENT_COPY.transactional} ${LEGAL_SUFFIX}`]
  // Only quote the promotional wording when they actually agreed to it —
  // otherwise the record would claim consent to marketing that was declined.
  if (promotional) shown.push(`Optional: ${SMS_CONSENT_COPY.promotional} ${LEGAL_SUFFIX}`)
  return shown.join(' ')
}

const LegalLinks: React.FC = () => (
  <>
    <a className={styles.link} href={AppConstants.TERMS_URL} target="_blank" rel="noreferrer">
      Terms of Service
    </a>
    {', '}
    <a className={styles.link} href={AppConstants.PRIVACY_URL} target="_blank" rel="noreferrer">
      Privacy Policy
    </a>
    .
  </>
)

const SmsConsent: React.FC<SmsConsentProps> = ({
  legacy,
  transactional,
  promotional,
  onTransactionalChange,
  onPromotionalChange,
}) => {
  if (legacy) {
    return <div className={styles.legacyConsent}>{SMS_CONSENT_COPY.legacy}</div>
  }

  return (
    <div className={styles.smsConsent}>
      <label className={styles.agreeContainer} htmlFor="sms-consent-transactional">
        <Checkbox
          id="sms-consent-transactional"
          name="smsConsent"
          sx={checkboxSx}
          checked={transactional}
          onChange={(e) => onTransactionalChange(e.target.checked)}
          size="medium"
        />
        <span className={styles.agreeText}>
          {SMS_CONSENT_COPY.transactional} Click for <LegalLinks />
        </span>
      </label>

      <label className={styles.agreeContainer} htmlFor="sms-consent-promotional">
        <Checkbox
          id="sms-consent-promotional"
          name="smsMarketingConsent"
          sx={checkboxSx}
          checked={promotional}
          onChange={(e) => onPromotionalChange(e.target.checked)}
          size="medium"
        />
        <span className={styles.agreeText}>
          <span className={styles.optional}>Optional:</span> {SMS_CONSENT_COPY.promotional} Click for <LegalLinks />
        </span>
      </label>
    </div>
  )
}

export default SmsConsent
